import * as keyset from 'i18n/SettingsLabels';
import i18n from '@yandex-int/i18n';
import { Example, SmartHomeDeepLinks } from './common';
import { Image } from './image';
import {
    AgeRestriction,
    BackendType,
    LanguageCode,
    RegionCode,
    AppVersionInfoWithTargetPlatforms,
    InstallableAppScreenshot,
    LicenseAgreement,
} from '../types';
import { ThereminvoxSoundMeta } from './thereminvox';
import { Channel } from './skill';

const t = i18n(keyset);

interface GrammarsObj {
    grammar: string;
}

export enum SkillAccess {
    Public = 'public',
    Hidden = 'hidden',
    Private = 'private',
}

const commonSkillAccesses = [
    {
        type: SkillAccess.Public,
        label: t('public'),
    },
    {
        type: SkillAccess.Hidden,
        label: t('hidden'),
    },
    {
        type: SkillAccess.Private,
        label: t('private'),
    },
];

const smartHomeSkillAccesses = [
    {
        type: SkillAccess.Public,
        label: t('public'),
    },
    {
        type: SkillAccess.Private,
        label: t('private'),
    },
];

export const skillAccesses: Record<Channel, Array<{ type: SkillAccess; label: string }>> = {
    [Channel.AliceSkill]: commonSkillAccesses,
    [Channel.AutoApp]: commonSkillAccesses,
    [Channel.SmartHome]: smartHomeSkillAccesses,
    [Channel.SmartScreenApp]: commonSkillAccesses,
    [Channel.Thereminvox]: commonSkillAccesses,
    [Channel.TvApp]: commonSkillAccesses,
};

export interface CommonSettings<BS extends BackendSettings, PS extends PublishingSettings> {
    name: string;
    activationPhrases: string[];
    backendSettings: BS;
    publishingSettings: PS;
    yaCloudGrant: boolean;
    logoId: string | null;
    logo2: Image | null;
    hideInStore: boolean;
    noteForModerator: string;
    requiredInterfaces: string[];
    exactSurfaces: string[];
    surfaceWhitelist: string[];
    surfaceBlacklist: string[];
    voice: string;
    oauthAppId: string;
    grammars: GrammarsObj;
    appMetricaApiKey: string;
    useStateStorage: boolean;
    rsyPlatformId: string;
    isTrustedSmartHomeSkill: boolean;
    customEntities: string;
    skillAccess: SkillAccess;
    enableAllAvailableRegions: boolean;
    selectedRegions: RegionCode[];
}

export type BackendSettings = object;

export interface AliceSkillBackendSettings extends BackendSettings {
    uri: string;
    functionId: string;
    backendType: BackendType;
}

export interface SmartHomeBackendSettings extends BackendSettings {
    uri: string;
    functionId: string;
    backendType: BackendType;
}

export interface MultilingualSetupEntry {
    description?: string;

    externalSettingsDescription: string;
    supportedUnitsDescription?: string;
    supportServiceUrl?: string;

    name: string;
    secondaryTitle?: string;
}

export interface PublishingSettings {
    brandIsVerified?: boolean;
    brandVerificationWebsite?: string;
    category?: string;
    developerName?: string;
    email?: string;
    emailIsVerified?: boolean;
    secondaryTitle?: string;
}

export interface AliceSkillPublishingSettings extends PublishingSettings {
    description?: string;
    explicitContent?: boolean;
    structuredExamples: Example[];
}

export interface SmartHomePublishingSettings extends PublishingSettings {
    description?: string;
    smartHome?: {
        isTrusted?: boolean;
        deepLinks?: SmartHomeDeepLinks;
    };
    multilingualSettings?: { [key in LanguageCode]?: MultilingualSetupEntry };
}

export interface SoundSet {
    sounds: ThereminvoxSoundMeta[];
    settings: {
        repeatSoundInside: boolean;
        noOverlaySamples: boolean;
        stopOnCeil: boolean;
    };
}

export interface ThereminvoxBackendSettings extends BackendSettings {
    soundSet?: SoundSet;
}

export interface ThereminvoxPublishingSettings extends PublishingSettings {
    explicitContent?: boolean;
}

export interface InstallableAppPublishingSettings extends PublishingSettings {
    description?: string;
}

export type AliceSkillSettings = CommonSettings<AliceSkillBackendSettings, AliceSkillPublishingSettings>;

export type SmartHomeSettings = CommonSettings<SmartHomeBackendSettings, SmartHomePublishingSettings>;

export type ThereminvoxSettings = CommonSettings<ThereminvoxBackendSettings, ThereminvoxPublishingSettings>;

export type InstallableAppSettings = {
    // refactor: rethink common settings and inherit
    activationPhrases: string[];
    packageName: string;
    noteForModerator: string;
    name: string;
    firstPublishedAt?: string;
    publishingSettings: InstallableAppPublishingSettings;
    ageRestriction?: AgeRestriction;
    logoId: string | null;
    apkInfoWithTargets?: AppVersionInfoWithTargetPlatforms;
    isTrusted: boolean;
    screenshots?: InstallableAppScreenshot[];
    privacyAgreement?: LicenseAgreement;
    eulaAgreement?: LicenseAgreement;
    secondaryTitle?: string;
    skillAccess: SkillAccess;
};

export type Settings = AliceSkillSettings | SmartHomeSettings | ThereminvoxSettings;
